import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Special, SpecialGroup, SystemTranslationKeys } from '@common/entities';

import { useSystemTranslation } from 'hooks';

interface Props {
  modalTitle: string;
  isOpen: boolean;
  onClose: () => void;
  specialGroup: SpecialGroup;
  selectedSpecials: Special[];
  handleSave: (specials: Special[], specialGroup: SpecialGroup) => void;
  isSaving?: boolean;
}
export function SpecialsModal(props: Props) {
  const { tSystem } = useSystemTranslation();
  const {
    specialGroup,
    isOpen,
    modalTitle,
    onClose,
    selectedSpecials = [],
    handleSave,
    isSaving = false,
  } = props;

  const [specials, setSpecials] = useState<Special[]>([...selectedSpecials]);

  function handleSpecialSelection(e: React.ChangeEvent<HTMLInputElement>, element: Special) {
    const isSelected = e.target.checked;

    if (isSelected) {
      setSpecials((prevState) => [...prevState, element]);
    } else {
      setSpecials((prevState) => prevState.filter((i) => i.specialId !== element.specialId));
    }
  }

  useEffect(() => {
    setSpecials(selectedSpecials);
  }, [selectedSpecials]);

  function handleSaveSpecials() {
    handleSave(specials, specialGroup);
  }

  const selectedIds = specials?.map((i) => i.specialId);
  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="black.200" backdropFilter="blur(12px) hue-rotate(0deg)" />
      <ModalContent background="white.base" borderRadius="8px">
        <ModalHeader
          background="blue.base"
          color="white.base"
          p={2}
          fontSize={16}
          borderTopLeftRadius="8px"
          borderTopRightRadius="8px"
        >
          {modalTitle}
        </ModalHeader>
        <ModalCloseButton
          aria-label={tSystem(SystemTranslationKeys.common.close)}
          color="white.base"
          isDisabled={isSaving}
        />
        <ModalBody minHeight={100}>
          <Box mt={5}>
            <Heading as="h5" size="sm">
              {specialGroup?.groupName}
            </Heading>
            <Text fontSize="xs" marginTop={5}>
              {specialGroup?.description}
            </Text>
            <Tooltip
              label={<Text whiteSpace="pre-line">{specialGroup?.termsAndConditions}</Text>}
              placement="top"
            >
              <Text textDecoration="underline" color="blue" fontSize="xs" marginTop={5}>
                {tSystem(SystemTranslationKeys.footer.termsAndConditions)}
              </Text>
            </Tooltip>
            <Text fontSize="xs" marginTop={5}>
              {tSystem(SystemTranslationKeys.common.selectSpecialOptions, {
                count: specialGroup?.specialLimit,
              })}
            </Text>
            <Box mt={5}>
              {specialGroup?.specials?.map((element) => {
                return (
                  <Flex
                    key={`element-list-${element.specialId}`}
                    justifyContent="space-between"
                    my={2}
                  >
                    <Checkbox
                      size="sm"
                      isChecked={selectedIds.includes(element.specialId)}
                      onChange={(e) => handleSpecialSelection(e, element)}
                      isDisabled={
                        Number(specialGroup?.specialLimit) === specials.length &&
                        !selectedIds.includes(element.specialId)
                      }
                    >
                      {element.specialName}
                    </Checkbox>
                  </Flex>
                );
              })}
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <Button
            size="sm"
            bg="silver.highlighthalf"
            color="black.dark"
            onClick={onClose}
            isDisabled={isSaving}
          >
            {tSystem(SystemTranslationKeys.common.cancel)}
          </Button>
          <Button
            size="sm"
            variant="brand"
            onClick={handleSaveSpecials}
            isLoading={isSaving}
            loadingText={tSystem(SystemTranslationKeys.common.saving)}
          >
            {tSystem(SystemTranslationKeys.common.save)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
